"use client";

import { useLayoutEffect } from "react";
import { usePathname } from "next/navigation";
import { phCapture } from "@/utils/analytics";

type ShiftSource = { node?: Node | null; previousRect: DOMRectReadOnly; currentRect: DOMRectReadOnly };
type LayoutShiftEntry = { value: number; hadRecentInput: boolean; startTime: number; sources?: readonly ShiftSource[] };
type Tally = { value: number; count: number; firstAtMs: number; movePx: number; height: number };
type RouteShifts = { pathname: string; startedAtMs: number; total: number; entries: number; targets: Map<string, Tally> };

const LAYOUT_SHIFT_SUMMARY_MIN_TOTAL = 0.05;
const LANDMARKS = new Set(["main", "nav", "section", "aside", "header", "footer"]);
const TOP_TARGETS = 3;

let observer: PerformanceObserver | null = null;
let routesBegun = 0;
let softNavigated = false;
let current: RouteShifts | null = null;

export function describeShiftTarget(node: Node | null | undefined): string {
  let el = node instanceof Element ? node : node?.parentElement ?? null;
  if (el === null) return "unknown";
  const parts: string[] = [];
  for (let depth = 0; el !== null && depth < 8 && parts.length < 3; depth += 1, el = el.parentElement) {
    const testId = el.getAttribute("data-testid");
    const tag = el.tagName.toLowerCase();
    const landmark = LANDMARKS.has(tag);
    const id = landmark ? el.id : "";
    const ariaLabel = landmark ? el.getAttribute("aria-label") : null;
    const role = el.getAttribute("role");
    const label = id ? `#${id}` : testId ? `[data-testid=${testId}]` : ariaLabel ? `[aria-label=${ariaLabel.slice(0, 40)}]` : role ? `[role=${role}]` : "";
    if (depth === 0 || label) parts.push(`${tag}${label}`);
  }
  return parts.join(" < ");
}

function documentPathname(): string | null {
  const navigation = performance.getEntriesByType("navigation")[0];
  return navigation === undefined ? null : new URL(navigation.name).pathname;
}

export function beginRoute(pathname: string): void {
  if (routesBegun > 0 || pathname !== documentPathname()) softNavigated = true;
  routesBegun += 1;
  current = { pathname, startedAtMs: softNavigated ? performance.now() : 0, total: 0, entries: 0, targets: new Map() };
}

export function recordShift(entry: LayoutShiftEntry): void {
  if (current === null || entry.hadRecentInput || entry.value === 0 || entry.startTime < current.startedAtMs) return;
  current.total += entry.value;
  current.entries += 1;
  const source = entry.sources?.find((candidate) => candidate.node instanceof Element) ?? entry.sources?.[0];
  const target = describeShiftTarget(source?.node);
  const tally = current.targets.get(target) ?? { value: 0, count: 0, firstAtMs: Math.round(entry.startTime - current.startedAtMs), movePx: 0, height: 0 };
  tally.value += entry.value;
  tally.count += 1;
  if (source !== undefined) {
    const moved = Math.max(Math.abs(source.currentRect.x - source.previousRect.x), Math.abs(source.currentRect.y - source.previousRect.y));
    tally.movePx = Math.max(tally.movePx, Math.round(moved));
    tally.height = Math.round(source.currentRect.height);
  }
  current.targets.set(target, tally);
}

export function endRoute(trigger: "route-change" | "hidden"): void {
  for (const entry of observer?.takeRecords() ?? []) recordShift(entry as unknown as LayoutShiftEntry);
  const route = current;
  if (route === null) return;
  const send = route.total >= LAYOUT_SHIFT_SUMMARY_MIN_TOTAL;
  if (trigger === "route-change") current = null;
  else if (send) current = { ...route, total: 0, entries: 0, targets: new Map() };
  if (!send) return;
  const top = [...route.targets.entries()]
    .filter(([, tally]) => tally.value >= 0.0001)
    .sort((a, b) => b[1].value - a[1].value)
    .slice(0, TOP_TARGETS)
    .map(([target, tally]) => ({ target, value: Number(tally.value.toFixed(4)), count: tally.count, first_at_ms: tally.firstAtMs, move_px: tally.movePx, height: tally.height }));
  phCapture("layout_shift_summary", {
    pathname: route.pathname,
    trigger,
    shift_total: Number(route.total.toFixed(4)),
    shift_entries: route.entries,
    route_age_ms: Math.round(performance.now() - route.startedAtMs),
    hard_load_route: route.startedAtMs === 0,
    viewport_width: window.innerWidth,
    top,
  }, trigger === "hidden" ? { send_instantly: true } : undefined);
}

function startObserving(): void {
  if (observer !== null || typeof PerformanceObserver === "undefined" || !PerformanceObserver.supportedEntryTypes?.includes("layout-shift")) return;
  observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) recordShift(entry as unknown as LayoutShiftEntry);
  });
  observer.observe({ type: "layout-shift", buffered: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") endRoute("hidden");
  });
}

export function LayoutShiftReporter() {
  const pathname = usePathname();
  useLayoutEffect(() => {
    if (pathname === null) return;
    startObserving();
    beginRoute(pathname);
    return () => endRoute("route-change");
  }, [pathname]);
  return null;
}
